import { useUtils } from "@/app/hooks/useUtils";
import { startCase } from "lodash";
import { hasLegacyNextSteps } from "../../ReferToOtherClinicComponent";
const RenderTimelineContent: React.FC<{
  emrSectionKey: string;
  emr: any;
}> = ({ emr, emrSectionKey }) => {
  const sectionTitleMap: Record<string, string> = {
    basic_checkup: "Basic Checkup",
    body_condition_score: "Body Score",
    dental_score: "Dental Score",
    subjective_history: "Subjective History",
    physical_examinations: "Physical Examination",
    assessment: "Assessment",
    diagnosis: "Diagnosis",
    tentative_diagnosis: "Tentative Diagnosis",
    definitive_diagnosis: "Definitive Diagnosis",
    lab_test_and_diagnostics: "Lab Test & Diagnostics",
    lab_test_recommended: "Recommended Lab Test",
    deworming_meds_treatments: "Deworming",
    treatments: "Treatments",
    medicines: "Medicines",
    food_and_diet: "Food & Diet",
    medicines_instructions: "Medical Instructions",
    medical_instructions: "Medical Instructions",
    follow_up: "Follow Up",
    next_steps: "Next Steps",
    prognosis: "Prognosis",
    pet_details: "Pet Details",
  };

  const {veticDateFormat} = useUtils();

  const section = emr[emrSectionKey];
  // "Refer to other doctor/clinic" (new EMRs) — top-level service_referrals, only ticked options are stored
  const referrals =
    emrSectionKey === "next_steps" && Array.isArray(emr?.service_referrals) ? emr.service_referrals : [];
  // Old Next Steps data takes priority; referrals render only when it is empty
  const hasReferrals = referrals.length > 0 && !hasLegacyNextSteps(section);

  const hasValue = (value: any): boolean => {
    if (value == null) return false;
    if (typeof value === 'string') return value.trim() !== '';
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return Object.keys(value).length > 0;
    return true;
  };

  const formatValue = (value: unknown): string => {
    if (Array.isArray(value)) return value.filter(Boolean).join(", ");
    if (typeof value === "string") return value;
    return "";
  };


  const hasData = () => {
    if (emrSectionKey === "next_steps") return hasReferrals || hasLegacyNextSteps(section);

    if (!section) return false;

    if (typeof section === 'string' && section.trim() === '') return false;

    if (Array.isArray(section) && section.length === 0) return false;

    if (typeof section === 'object' && !Array.isArray(section)) {
      switch (emrSectionKey) {
        case "basic_checkup":
          return Object.keys(section).length > 0;

        case "lab_test_and_diagnostics":
          return (section?.lab_test_added?.length > 0 || section?.lab_test_recommended?.length > 0);

        case "pet_details":
          return !!(section?.pet_temperature || section?.pet_weight_kg);

        case "treatments":
          return !!(section?.treatments_added?.length > 0 || typeof section?.treatments_instructions === "string" && section?.treatments_instructions?.trim());

        case "medicines":
          return !!(section?.medicines_added?.length > 0 || typeof section?.medicines_instructions === "string" && section?.medicines_instructions?.trim());

        case "food_and_diet":
          return section?.food_added?.length > 0;

        case "follow_up":
          return !!(hasValue(section?.follow_up_service) || hasValue(section?.follow_up_date) || hasValue(section?.follow_up_comment));
      }
    }

    return true;
  };

  if (!hasData()) return null;

  const renderValue = () => {
    switch (emrSectionKey) {
      case "basic_checkup":
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2">
            {Object.keys(section).map((key) => (
              <div key={key} className="flex gap-2">
                <span className="text-theme-xs text-gray-500 uppercase">
                  {startCase(key)}:
                </span>
                <span className="text-theme-xs font-medium text-gray-800">
                  {section[key]?.status || "Missing Data"}
                </span>
              </div>
            ))}
          </div>
        );

      case "body_condition_score":
      case "dental_score":
      case "subjective_history":
      case "physical_examinations":
      case "assessment":
      case "diagnosis":
        return (
          <p
            className="text-theme-xs text-gray-700"
            style={{ whiteSpace: "pre-line" }}
          >
            {section}
          </p>
        );

      case "tentative_diagnosis":
      case "definitive_diagnosis":
        return (
          <ul className="list-disc pl-4 space-y-0.5">
            {(section as string[])?.map((diagnosis: string, index: number) => (
              <li key={index} className="text-theme-xs text-gray-700">
                {diagnosis}
              </li>
            ))}
          </ul>
        );
      case "prognosis":
      case "medical_instructions":
        return (
          <p
            className="text-theme-xs text-gray-700"
            style={{ whiteSpace: "pre-line" }}
          >
            {section}
          </p>
        );

      case "general_appearance":
        return (
          <p className="text-theme-xs text-gray-700">
            <span className="font-semibold text-black">General Appearance:</span> {section}
          </p>
        );

      case "appetite":
        return (
          <p className="text-theme-xs text-gray-700">
            <span className="font-semibold text-black">Appetite:</span> {section}
          </p>
        );

      case "cmm":
        return (
          <p className="text-theme-xs text-gray-700">
            <span className="font-semibold text-black">CMM:</span> {section}
          </p>
        );

      case "lymph_nodes":
        return (
          <p className="text-theme-xs text-gray-700">
            <span className="font-semibold text-black">Lymph Nodes:</span> {section}
          </p>
        );

      case "skin":
        return (
          <p className="text-theme-xs text-gray-700">
            <span className="font-semibold text-black">Skin:</span> {section}
          </p>
        );

      case "body":
        return (
          <p className="text-theme-xs text-gray-700">
            <span className="font-semibold text-black">Body:</span> {section}
          </p>
        );

      case "paws":
        return (
          <p className="text-theme-xs text-gray-700">
            <span className="font-semibold text-black">Paws:</span> {section}
          </p>
        );

      case "ears":
        return (
          <p className="text-theme-xs text-gray-700">
            <span className="font-semibold text-black">Ears:</span> {section}
          </p>
        );

      case "lab_test_and_diagnostics":
        return (
          <div className="space-y-6">

            {/* Added Tests */}
            {section?.lab_test_added?.length > 0 && (
              <div>
                <p className="font-semibold text-theme-xs mb-2">Added Tests</p>

                <div className="border rounded-lg overflow-hidden">
                  <div className="grid grid-cols-[2.5fr_1fr_1fr] bg-gray-100 text-theme-xs font-semibold text-gray-700 px-4 py-3 ">
                    <span>Test</span>
                    <span className="text-center">Qty</span>
                    <span>Remark</span>
                  </div>

                  <div className="">
                    {section.lab_test_added.map((item: any, index: number) => (
                      <div
                        key={index}
                        className="grid grid-cols-[2.5fr_1fr_1fr] px-4 py-3 text-theme-xs  border-gray-200 bg-white border-t"
                      >
                        <span className="font-medium text-gray-800">
                          {item?.name}
                        </span>

                        <span className="text-gray-600 text-center">
                          {item?.quantity || "-"}
                        </span>

                        <span className="text-gray-500">
                          {item?.remark || "-"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Recommended Tests */}
            {section?.lab_test_recommended?.length > 0 && (
              <div>
                <p className="font-semibold text-theme-xs mb-2">Recommended Tests</p>

                <div className="border rounded-lg overflow-hidden">
                  <div className="grid grid-cols-[2.5fr_1fr_1fr] bg-gray-100 text-theme-xs font-semibold text-gray-700 px-4 py-3 ">
                    <span>Test</span>
                    <span className="text-center">Qty</span>
                    <span>Remark</span>
                  </div>

                  <div className="">
                    {section.lab_test_recommended.map((item: any, index: number) => (
                      <div
                        key={index}
                        className="grid grid-cols-[2.5fr_1fr_1fr] px-4 py-3 text-theme-xs border-t border-gray-200 bg-white"
                      >
                        <span className="font-medium text-gray-800">
                          {item?.name}
                        </span>

                        <span className="text-gray-600 text-center">
                          {item?.quantity || "-"}
                        </span>

                        <span className="text-gray-500">
                          {item?.remark || "-"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      case "pet_details":
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2">
            {section?.pet_temperature && (
              <div className="flex gap-2">
                <span className="text-theme-xs uppercase text-gray-500">Temperature:</span>
                <span className="font-medium">{section?.pet_temperature}°F</span>
              </div>
            )}

            {section?.pet_weight_kg && (
              <div className="flex gap-2">
                <span className="text-theme-xs uppercase text-gray-500">Weight:</span>
                <span className="font-medium">{section?.pet_weight_kg} Kg</span>
              </div>
            )}
          </div>
        );
      case "treatments":
        return (
          <div className="space-y-5">

            {/* Treatments Added */}
            {section?.treatments_added?.length > 0 && (
              <div>
                {/* <p className="font-semibold text-theme-sm mb-2">Treatments</p> */}

                <div className="border rounded-lg overflow-hidden">
                  <div className="grid grid-cols-[2.5fr_1fr_1fr] bg-gray-100 text-theme-xs font-semibold text-gray-700 px-4 py-3 ">
                    <span>Treatment</span>
                    <span className="text-center">Qty</span>
                    <span>Remark</span>
                  </div>

                  <div className="">
                    {section.treatments_added.map((item: any, index: number) => (
                      <div
                        key={index}
                        className="grid grid-cols-[2.5fr_1fr_1fr] px-4 py-3 text-theme-xs border-t border-gray-200 bg-white"
                      >
                        <span className="font-medium text-gray-800">
                          {item?.name}
                        </span>

                        <span className="text-gray-600 text-center">
                          {item?.quantity || "-"}
                        </span>

                        <span className="text-gray-500">
                          {item?.remark || "-"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Instructions */}
            {section?.treatments_instructions?.trim() && (
              <div>
                <p className="font-semibold text-theme-sm mb-2">
                  Treatment Instructions
                </p>

                <div className="border rounded-lg p-4 text-theme-xs text-gray-700 whitespace-pre-line bg-white">
                  {section.treatments_instructions}
                </div>
              </div>
            )}
          </div>
        );
      case "medicines":
        return (
          <div className="space-y-5">

            {section?.medicines_added?.length > 0 && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">

                {/* scroll wrapper */}
                <div className="overflow-x-auto">

                  <div className="min-w-[860px]">

                    {/* Header */}
                    <div className="grid grid-cols-[minmax(0,2fr)_80px_120px_130px_130px_120px_120px] bg-gray-100 text-gray-700 text-theme-xs font-semibold px-4 py-3">
                      <span>Medicine</span>
                      <span className="text-center" >Qty</span>
                      <span className="text-center" >Dose</span>
                      <span className="text-center" >Frequency</span>
                      <span className="text-center" >Timing</span>
                      <span className="text-center" >Duration</span>
                      <span>Remark</span>
                    </div>

                    {/* Rows */}
                    {section.medicines_added.map((item: any, index: number) => (
                      <div key={index} className="px-4 py-3 border-b last:border-0 text-theme-xs space-y-2 bg-white">

                        <div className="grid grid-cols-[minmax(0,2fr)_80px_120px_130px_130px_120px_120px] items-center ">

                          <span className="font-medium text-gray-800">
                            {item?.name}
                          </span>

                          <span className="text-gray-600 text-center">
                            {item?.quantity || "-"}
                          </span>

                          <span className="text-gray-600 text-center">
                            {item?.dose ? `${item.dose} ${item?.dose_unit ?? ""}` : "-"}
                          </span>

                          <span className="text-gray-600 text-center">
                            {item?.frequency || "-"}
                          </span>

                          <span className="text-gray-600 text-center">
                            {item?.timing || "-"}
                          </span>

                          <span className="text-gray-600 text-center">
                            {item?.duration
                              ? `${item.duration} ${item?.duration_unit ?? ""}`
                              : "-"}
                          </span>

                          <span className="text-gray-500">
                            {item?.remark || "-"}
                          </span>

                        </div>

                        {item?.tapering_doses?.length > 0 && (
                          <div className="ml-4 pl-4 border-l-2 border-gray-200 text-gray-600 bg-white">

                            <p className="text-[11px] font-semibold text-gray-500 mb-1 uppercase">
                              Tapering Doses
                            </p>

                            <ul className="space-y-1">
                              {item.tapering_doses.map((dose: any, i: number) => (
                                <li key={i} className="flex flex-wrap gap-x-2">
                                  <span>
                                    {dose?.dose ? `${dose.dose} ${dose?.dose_unit ?? ""}` : ""}
                                  </span>

                                  {dose?.frequency && (
                                    <span>• Frequency: {dose.frequency}</span>
                                  )}

                                  {dose?.timing && (
                                    <span>• Timing: {dose.timing}</span>
                                  )}

                                  {dose?.duration && (
                                    <span>• {dose.duration} {dose?.duration_unit ?? ""}</span>
                                  )}

                                  {dose?.remark && (
                                    <span className="text-gray-500">• {dose.remark}</span>
                                  )}
                                </li>
                              ))}
                            </ul>

                          </div>
                        )}

                      </div>
                    ))}

                  </div>
                </div>
              </div>
            )}

             {section?.medicines_instructions?.trim() && (
              <div>
                <p className="font-semibold text-theme-sm mb-2">
                  Medicine Instructions
                </p>

                <div className="border rounded-lg p-4 text-theme-xs text-gray-700 whitespace-pre-line bg-white">
                  {section.medicines_instructions}
                </div>
              </div>
            )}

          </div>
        );
      case "food_and_diet":
        return (
          <div className="border border-gray-200 rounded-lg overflow-hidden">

            {/* Header */}
            <div className="grid grid-cols-[2.5fr_1fr_1fr] bg-gray-100 text-theme-xs font-semibold text-gray-700 px-4 py-3 ">
              <span>Food</span>
              <span className="text-center">Qty </span>
              <span>Remark</span>
            </div>

            {/* Rows */}
            {section?.food_added?.map((item: any, index: number) => (
              <div
                key={index}
                className="grid grid-cols-[2.5fr_1fr_1fr] px-4 py-3 text-theme-xs border-t border-gray-200 bg-white"
              >
                <span className="font-medium text-gray-800">
                  {item?.name}
                </span>

                <span className="text-gray-600 text-center">
                  {item?.quantity || "-"}
                </span>

                <span className="text-gray-600">
                  {item?.remark || ""}
                </span>
              </div>
            ))}

          </div>
        );
      case "deworming_meds_treatments":
        return (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="grid grid-cols-[2.5fr_1fr_1fr] bg-gray-100 text-theme-xs font-semibold text-gray-700 px-4 py-3 ">
              <span>Deworming</span>
              <span className="text-center">Qty </span>
              <span>Remark</span>
            </div>

            {section?.map((item: any, index: number) => (
              <div
                key={index}
                className="grid grid-cols-[2.5fr_1fr_1fr] px-4 py-3 text-theme-xs border-t border-gray-200 bg-white"
              >
                <span className="font-medium text-gray-800">
                  {item?.name}
                </span>

                <span className="text-gray-600 text-center">
                  {item?.quantity || "-"}
                </span>

                <span className="text-gray-600">
                  {item?.remark || "-"}
                </span>
              </div>
            ))}
          </div>
        );
      case "follow_up":
        return (
          <div className="border border-gray-200 rounded-lg overflow-hidden">

            {/* Header */}
            <div className="grid grid-cols-[1fr_1fr_1fr] bg-gray-100 text-theme-xs font-semibold text-gray-700 px-4 py-3 ">
              <span>Service</span>
              <span >Date</span>
              <span>Comment</span>
            </div>

            {/* Row */}
            <div className="grid grid-cols-[1fr_1fr_1fr] px-4 py-3 text-theme-xs border-t border-gray-200 bg-white">

              <span className="font-medium text-gray-800">
                {formatValue(section?.follow_up_service) || "-"}
              </span>

              <span className="text-gray-600">
                {veticDateFormat(section?.follow_up_date) || "-"}
              </span>

              <span className="text-gray-600">
                {section?.follow_up_comment || "-"}
              </span>

            </div>

          </div>
        );

      case "next_steps":
        if (hasReferrals) {
          return (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="grid grid-cols-[1fr_2fr] bg-gray-100 text-theme-xs font-semibold text-gray-700 px-4 py-3">
                <span>Referred Service</span>
                <span>Notes</span>
              </div>
              {referrals.map((referral: any) => (
                <div
                  key={`${referral?.service_type_id}:${referral?.service_type}`}
                  className="grid grid-cols-[1fr_2fr] px-4 py-3 text-theme-xs border-t border-gray-200 bg-white"
                >
                  <span className="font-medium text-gray-800">
                    {referral?.service_type || "-"}
                  </span>
                  <span className="text-gray-600" style={{ whiteSpace: "pre-line" }}>
                    {referral?.notes || "-"}
                  </span>
                </div>
              ))}
            </div>
          );
        }
        return (
          <div className="border border-gray-200 rounded-lg overflow-hidden">

            {/* Header */}
            <div className="grid grid-cols-[1fr_1fr_1fr] bg-gray-100 text-theme-xs font-semibold text-gray-700 px-4 py-3 ">
              <span>Suggested Services</span>
              <span>Surgery Date</span>
              <span>Refer To</span>
            </div>

            {/* Row */}
            <div className="grid grid-cols-[1fr_1fr_1fr] px-4 py-3 text-theme-xs border-t border-gray-200 bg-white">

              <span className="text-gray-800">
                {formatValue(section?.suggested_services) || "-"}
              </span>

              <span className="text-gray-600">
                {veticDateFormat(section?.surgery_date) || "-"}
              </span>

              <span className="text-gray-600">
                {section?.refer_clinic_name || "-"}
              </span>

            </div>

          </div>
        );

      default:
        return null;
    }
  };
  return (
    <>
      <h3 className="text-theme-sm font-semibold text-gray-800 mt-3">
        {emrSectionKey === "next_steps" && hasReferrals ? "Refer to other doctor/clinic" : sectionTitleMap[emrSectionKey]}
      </h3>

      <div className="mt-1 text-theme-xs text-gray-700">
        {renderValue()}
      </div>
    </>
  );
};
export default RenderTimelineContent;
